import React, { useEffect, useMemo, useState } from "react";

/* --- Brain-age estimate (for fun only) --- */
function brainAgeFromScore(score, total) {
  const pct = (score / total) * 100;
  if (pct === 100) return "≈ 18 years — razor-sharp!";
  if (pct >= 90)  return "≈ 20–25 years — very quick thinker.";
  if (pct >= 75)  return "≈ 26–35 years — sharp and focused.";
  if (pct >= 60)  return "≈ 36–45 years — steady and practical.";
  if (pct >= 40)  return "≈ 46–60 years — thoughtful (warm up a bit!)";
  return "≈ 61+ years — wise, but out of practice 😉";
}

/* --- Quiz content (10 questions) --- */
const QUESTIONS = [
  {
    q: "8836 — 5, 5116 — 1, 2232 — 0, 3366 — ?",
    options: ["2", "0", "4", "3"],
    a: 0,
    expl:
      "Count ‘holes’ in the digits (8→2, 6→1, 3→0). 3+3+6+6 → 0+0+1+1 = 2.",
  },
  {
    q: "There are 8 benches in a park. Three were freshly painted. How many benches are in the park now?",
    options: ["Eleven", "Eight", "Ten", "Three"],
    a: 1,
    expl: "Painting doesn’t change the count — still 8.",
  },
  {
    q: "Which human organ produces insulin?",
    options: ["Brain", "Heart", "Liver", "Pancreas"],
    a: 3,
    expl: "Insulin is produced by the pancreas.",
  },
  {
    q: "How many times can you subtract 3 from 25?",
    options: ["Eight", "Two", "One", "Five"],
    a: 2,
    expl: "Only once — after that you subtract from 22.",
  },
  {
    q: "What do you call the ray that starts at a vertex and splits an angle into two equal parts?",
    options: ["Altitude", "Radius", "Median", "Angle bisector"],
    a: 3,
    expl: "That ray is an angle bisector.",
  },
  {
    q: "Marina is 22 and Sergey is 25. What will the age difference be in three years?",
    options: ["Two", "Six", "Three", "Nine"],
    a: 2,
    expl: "Age difference stays the same: 3 years.",
  },
  {
    q: "A dog has one, a moose has two, a cheetah has none — how many does a raccoon have?",
    options: ["One", "Two", "Three", "None"],
    a: 1,
    expl: "Count the letter “o” in the English names: dog→1, moose→2, cheetah→0, raccoon→2.",
  },
  {
    q: "How many times per day do the minute and hour hands form a right angle (90°)?",
    options: ["44", "30", "34", "40"],
    a: 0,
    expl: "They make a right angle 44 times a day.",
  },
  {
    q: "What is the square root of 144?",
    options: ["4", "12", "14", "17"],
    a: 1,
    expl: "√144 = 12.",
  },
  {
    q: "A house has pets: rabbits and cats. Exactly one pet is not a cat, and all pets except one are rabbits. How many pets are there?",
    options: ["Two", "Three", "Four", "Five"],
    a: 0,
    expl: "Two pets: one rabbit and one cat. Both statements are then true.",
  },
];

export default function BrainAgeQuiz({ title = "Quick Brain-Age Test", onFinish }) {
  /* Helpers */
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  /* State */
  const [order, setOrder] = useState(() => shuffle([...Array(QUESTIONS.length).keys()]));
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [checked, setChecked] = useState(Array(QUESTIONS.length).fill(false));
  const [score, setScore] = useState(0);

  const total = QUESTIONS.length;
  const qIdx = order[idx];
  const q = QUESTIONS[qIdx];
  const picked = answers[qIdx];

  /* Keyboard shortcuts */
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (k === "n") next();
      if (k === "p") prev();
      if (k === "r") restart();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, order, answers, checked, score]);

  const progressPct = Math.round(((idx + 1) / total) * 100);
  const finishedPct = Math.round((checked.filter(Boolean).length / total) * 100);

  /* Actions */
  function selectOption(i) {
    setAnswers((prev) => {
      const clone = [...prev];
      clone[qIdx] = i;
      return clone;
    });
  }
  function submitCurrent() {
    if (checked[qIdx]) return;
    setChecked((prev) => {
      const c = [...prev];
      c[qIdx] = true;
      return c;
    });
    if (picked === q.a) setScore((s) => s + 1);
    if (checked.filter(Boolean).length + 1 === total && typeof onFinish === "function") {
      onFinish({ score: picked === q.a ? score + 1 : score, total });
    }
  }
  function next() {
    if (idx < total - 1) setIdx((i) => i + 1);
  }
  function prev() {
    if (idx > 0) setIdx((i) => i - 1);
  }
  function restart() {
    setOrder(shuffle([...Array(QUESTIONS.length).keys()]));
    setIdx(0);
    setAnswers(Array(QUESTIONS.length).fill(null));
    setChecked(Array(QUESTIONS.length).fill(false));
    setScore(0);
  }

  return (
    <div className="card shadow-sm border-0 brain-quiz" style={{ maxWidth: 900 }}>
      <div className="card-body">
        <h2 className="h5 mb-1">{title}</h2>
        <div className="text-muted small mb-3">
          10 questions · press <code>N</code> (next), <code>P</code> (prev), <code>R</code> (restart)
        </div>

        {/* Top bar */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
          <div>
            <span className="badge text-bg-light">
              Question <strong>{idx + 1}</strong> / <strong>{total}</strong>
            </span>{" "}
            <span className="badge text-bg-light ms-1">
              Score: <strong>{score}</strong>
            </span>
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={restart} title="Restart (R)">
            Restart
          </button>
        </div>

        {/* Progress */}
        <div className="progress mb-2" style={{ height: 8 }}>
          <div className="progress-bar" role="progressbar" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="progress mb-3" style={{ height: 8 }}>
          <div className="progress-bar bg-success" role="progressbar" style={{ width: `${finishedPct}%` }} />
        </div>

        {/* Question */}
        <div className="mb-2 fw-semibold">
          Q{idx + 1}: {q.q}
        </div>

        {/* Options (divs so clicks can't be swallowed by forms) */}
        <div className="list-group">
          {q.options.map((opt, i) => {
            const isPicked = picked === i;
            const isChecked = checked[qIdx];
            const isCorrect = i === q.a;
            const showSuccess = isChecked && isPicked && isCorrect;
            const showWrong = isChecked && isPicked && !isCorrect;

            return (
              <div
                key={i}
                role="button"
                tabIndex={0}
                className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                style={{
                  cursor: isChecked ? "default" : "pointer",
                  borderRadius: 8,
                  backgroundColor: showSuccess
                    ? "var(--bs-success-bg-subtle,#e9f7ef)"
                    : showWrong
                    ? "var(--bs-danger-bg-subtle,#fdecef)"
                    : "var(--bs-body-bg,#fff)",
                  borderColor: showSuccess
                    ? "var(--bs-success,#198754)"
                    : showWrong
                    ? "var(--bs-danger,#dc3545)"
                    : "var(--bs-border-color,#e5e7eb)",
                  marginBottom: 8,
                }}
                onClick={() => !isChecked && selectOption(i)}
                onKeyDown={(e) => {
                  if (!isChecked && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    selectOption(i);
                  }
                }}
              >
                <span>{opt}</span>
                {isChecked && (
                  <span className={"ms-2 badge " + (isCorrect ? "text-bg-success" : "text-bg-danger")}>
                    {isCorrect ? "Correct" : "Incorrect"}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-primary" onClick={submitCurrent}>
            {checked[qIdx] ? "Submitted" : "Submit"}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={prev} disabled={idx === 0}>
            Prev (P)
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={next}
            disabled={idx === total - 1}
          >
            Next (N)
          </button>
        </div>

        {/* Explanation for current question */}
        {checked[qIdx] && (
          <div className="mt-3 p-3 rounded-3 border" style={{ background: "#f8fafc", borderColor: "#cbd5e1" }}>
            <div className="fw-semibold mb-1">Answer &amp; Explanation</div>
            <div className="small">
              Correct answer: <strong>{q.options[q.a]}</strong>
            </div>
            {q.expl && <div className="small text-muted mt-1">{q.expl}</div>}
          </div>
        )}

        {/* Final results */}
        {checked.every(Boolean) && (
          <>
            <hr />
            <ResultsPanel score={score} total={total} answers={answers} order={order} />
          </>
        )}
      </div>
    </div>
  );
}

/* Results list with explanations for all questions */
function ResultsPanel({ score, total, answers, order }) {
  const brainAge = useMemo(() => brainAgeFromScore(score, total), [score, total]);
  const pct = Math.round((score / total) * 100);

  return (
    <div>
      <h3 className="h5">Results</h3>
      <div className="mb-2">
        <strong>Your score: {score} / {total}</strong> ({pct}%)
      </div>
      <div className="mb-3">
        <span className="badge text-bg-success">Estimated brain age: {brainAge}</span>
      </div>

      <div className="list-group">
        {order.map((qIdx, i) => {
          const QQ = QUESTIONS[qIdx];
          const got = answers[qIdx] === QQ.a;
          return (
            <div
              key={i}
              className="list-group-item"
              style={{
                borderRadius: 8,
                backgroundColor: got ? "var(--bs-success-bg-subtle,#e9f7ef)" : "var(--bs-danger-bg-subtle,#fdecef)",
                borderColor: got ? "var(--bs-success,#198754)" : "var(--bs-danger,#dc3545)",
                marginBottom: 8,
              }}
            >
              <div className="fw-semibold">Q{i + 1}: {QQ.q}</div>
              <div className="small">
                <em>{got ? "✓ Correct" : "✗ Incorrect"}</em> — Answer: <strong>{QQ.options[QQ.a]}</strong>
              </div>
              {QQ.expl && <div className="small text-muted mt-1">{QQ.expl}</div>}
            </div>
          );
        })}
      </div>

      <p className="text-muted small mt-3">
        For entertainment only — not a scientific assessment.
      </p>
    </div>
  );
}

/* Optional CSS guard (add to your global CSS if clicks are blocked by a parent overlay)
.brain-quiz, .brain-quiz * { pointer-events: auto !important; }
*/